import { Maximize2, ZoomIn, ZoomOut } from 'lucide-react'
import type { ReactNode } from 'react'
import { EDGE_STYLES, CATEGORY_COLORS, DEFAULT_NODE_COLOR } from './graph-canvas-styles'

interface GraphCanvasControlsProps {
  onZoomIn: () => void
  onZoomOut: () => void
  onFit: () => void
}

export function GraphCanvasControls({ onZoomIn, onZoomOut, onFit }: GraphCanvasControlsProps) {
  return (
    <div className="absolute bottom-4 right-4 flex flex-col gap-1">
      <IconControl label="Zoom in" onClick={onZoomIn}>
        <ZoomIn size={14} />
      </IconControl>
      <IconControl label="Zoom out" onClick={onZoomOut}>
        <ZoomOut size={14} />
      </IconControl>
      <IconControl label="Fit graph" onClick={onFit}>
        <Maximize2 size={14} />
      </IconControl>
    </div>
  )
}

export function GraphLegend() {
  return (
    <div className="absolute bottom-4 left-4 hidden rounded-lg bg-surface-1/90 p-3 backdrop-blur-sm sm:block">
      <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-neutral-500">Node Types</div>
      <div className="mb-2 flex flex-col gap-1">
        {Object.entries(CATEGORY_COLORS).map(([cat, color]) => (
          <LegendRow key={cat} label={cat} color={color} />
        ))}
        <LegendRow label="other" color={DEFAULT_NODE_COLOR} />
      </div>
      <div className="mb-1.5 text-[10px] font-medium uppercase tracking-wider text-neutral-500">Edge Types</div>
      <div className="flex flex-col gap-1">
        {Object.entries(EDGE_STYLES).map(([type, style]) => (
          <div key={type} className="flex items-center gap-2">
            <div
              className="h-0.5 w-4"
              style={{
                backgroundColor: style.color,
                borderStyle: style.lineStyle === 'dashed' ? 'dashed' : 'solid',
              }}
            />
            <span className="text-[10px] text-neutral-400">{type}</span>
          </div>
        ))}
        <div className="flex items-center gap-2">
          <div className="h-0.5 w-4 border-t border-dotted border-neutral-600" />
          <span className="text-[10px] text-neutral-400">similarity</span>
        </div>
      </div>
    </div>
  )
}

function IconControl({ label, onClick, children }: { label: string; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex h-8 w-8 items-center justify-center rounded-md bg-surface-2 text-neutral-300 hover:bg-surface-3 hover:text-neutral-100"
      title={label}
      aria-label={label}
    >
      {children}
    </button>
  )
}

function LegendRow({ label, color }: { label: string; color: string }) {
  return (
    <div className="flex items-center gap-2">
      <div className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: color }} />
      <span className="text-[10px] text-neutral-400">{label}</span>
    </div>
  )
}
